import { useEffect, useState } from 'react';
import type { GameInfo, RatingRow } from '@bn/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Alert, Card, Spinner } from '@/components/ui';

export function LeaderboardPage() {
  const { user } = useAuth();
  const [games, setGames] = useState<GameInfo[]>([]);
  const [gameId, setGameId] = useState<string>('');
  const [rows, setRows] = useState<RatingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<{ games: GameInfo[] }>('/api/games')
      .then((r) => {
        setGames(r.games);
        setGameId((current) => current || (r.games[0]?.id ?? ''));
      })
      .catch(() => setError('Impossible de charger la liste des jeux.'));
  }, []);

  useEffect(() => {
    if (!gameId) return;
    setRows(null);
    void api
      .get<{ rows: RatingRow[] }>(`/api/leaderboard?gameId=${encodeURIComponent(gameId)}`)
      .then((r) => setRows(r.rows))
      .catch(() => setError('Impossible de charger le classement.'));
  }, [gameId]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Classement</h1>
          <p className="text-sm text-slate-400">
            Points ELO, départ à 1200, mise à jour à chaque fin de partie.
          </p>
        </div>
        <label className="text-sm text-slate-300">
          <span className="mr-2">Jeu</span>
          <select
            value={gameId}
            onChange={(e) => setGameId(e.target.value)}
            className="rounded-lg border border-slate-600 bg-slate-900 px-3 py-1.5 text-slate-100"
          >
            {games.map((game) => (
              <option key={game.id} value={game.id}>
                {game.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <Alert>{error}</Alert>}

      <Card>
        {rows === null ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-400">
            Aucune partie jouée pour l’instant.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[32rem] text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-left text-slate-400">
                  <th className="py-2 pr-3 font-medium">#</th>
                  <th className="py-2 pr-3 font-medium">Joueur</th>
                  <th className="py-2 pr-3 text-right font-medium">Points</th>
                  <th className="py-2 pr-3 text-right font-medium">Parties</th>
                  <th className="py-2 pr-3 text-right font-medium">V / D</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr
                    key={row.userId}
                    className={`border-b border-slate-800/60 ${
                      row.userId === user?.id ? 'bg-sky-500/10 text-sky-100' : ''
                    }`}
                  >
                    <td className="py-2 pr-3 text-slate-400">{index + 1}</td>
                    <td className="py-2 pr-3 font-medium">{row.displayName}</td>
                    <td className="py-2 pr-3 text-right font-mono">{row.rating}</td>
                    <td className="py-2 pr-3 text-right text-slate-400">{row.played}</td>
                    <td className="py-2 pr-3 text-right text-slate-400">
                      {row.wins} / {row.losses}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
